/* ==========================================================================
   Lewesah storefront behaviour.

   The design kit ran on React, Babel-in-the-browser and a CDN icon library.
   None of that belongs on a storefront, so every interaction it described is
   reimplemented here in plain DOM: gallery, variant picking, quantity,
   dialogs, sort. No dependencies, no build step, ~6KB.

   Everything is delegated from `document`, so sections re-rendered by the
   theme editor keep working without re-initialisation.
   ========================================================================== */

(function () {
  'use strict';

  var money = { format: window.LEWESAH_MONEY_FORMAT || '{{amount}} EGP' };

  /* --- helpers ---------------------------------------------------------- */

  function closest(el, selector) {
    return el && el.closest ? el.closest(selector) : null;
  }

  function formatMoney(cents) {
    var amount = (cents / 100).toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
    });
    return amount;
  }

  function currencyCode() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EGP';
  }

  function priceMarkup(cents, compareAt) {
    var html =
      '<span class="lw-price lw-price--sm"><span class="lw-price__amount">' +
      formatMoney(cents) +
      '<span class="lw-price__currency"> ' +
      currencyCode() +
      '</span></span>';
    if (compareAt && compareAt > cents) {
      html +=
        '<s class="lw-price__compare">' + formatMoney(compareAt) + ' ' + currencyCode() + '</s>';
    }
    return html + '</span>';
  }

  /* --- gallery ---------------------------------------------------------- */

  /* Shots are matched by their position in the gallery rather than a data
     attribute: Liquid parses a hyphen inside a filter argument as
     subtraction, so `data-lw-shot:` cannot be passed to image_tag at all. */
  function selectShot(root, index) {
    var shots = root.querySelectorAll('.lw-gallery__shot');
    var thumbs = root.querySelectorAll('[data-lw-thumb]');

    for (var i = 0; i < shots.length; i++) {
      shots[i].classList.toggle('is-active', String(i) === String(index));
    }
    for (var j = 0; j < thumbs.length; j++) {
      var active = thumbs[j].getAttribute('data-lw-thumb') === String(index);
      thumbs[j].classList.toggle('is-active', active);
      thumbs[j].setAttribute('aria-current', active ? 'true' : 'false');
    }
  }

  /* --- colour filtering -------------------------------------------------

     A product carries every colour's shots in one media list. Shopify allows a
     variant exactly one featured image, so there is no native way to swap a
     whole gallery on colour — the built-in behaviour would change the hero shot
     and leave the rest showing the previous colour.

     So the section emits an index-to-colour map (from each image's alt text) and
     we filter against it. Untagged shots have an empty colour and stay visible
     for every selection, which keeps products without a colour option working
     exactly as before. */

  /* Sentinel the section uses for media with no colour in its alt. Must stay in
     sync with lewesah-product.liquid. An empty string cannot be used: Liquid's
     `split` drops trailing empties, which would misalign the index map. */
  var UNTAGGED = '-';

  function shotColours(root) {
    var node = root.querySelector('[data-lw-shot-colours]');
    if (!node) return [];
    try {
      return JSON.parse(node.textContent) || [];
    } catch (e) {
      return [];
    }
  }

  /* Which option position is the colour? Read it off the swatch row rather
     than hard-coding 1, so reordering the options in the admin can't break it. */
  function colourPosition(root) {
    var swatch = root.querySelector('.lw-colors [data-lw-option]');
    if (!swatch) return null;
    var pos = parseInt(swatch.getAttribute('data-lw-option'), 10);
    return isNaN(pos) ? null : pos;
  }

  function applyColourFilter(root, colour) {
    var colours = shotColours(root);
    if (!colours.length || !colour) return;

    var thumbs = root.querySelectorAll('[data-lw-thumb]');
    var firstIndex = -1;

    for (var i = 0; i < colours.length; i++) {
      var matches = colours[i] === UNTAGGED || colours[i] === colour;
      if (matches && firstIndex === -1) firstIndex = i;
    }

    /* Every colour should have shots. If the alt text ever stops matching an
       option value we would hide the entire gallery, so bail instead. */
    if (firstIndex === -1) return;

    for (var t = 0; t < thumbs.length; t++) {
      var idx = parseInt(thumbs[t].getAttribute('data-lw-thumb'), 10);
      var visible = colours[idx] === UNTAGGED || colours[idx] === colour;
      thumbs[t].hidden = !visible;
    }

    selectShot(root, firstIndex);
  }

  /* Liquid already marks the first shot active, so a JS failure still leaves
     a visible gallery. This only repairs the case where markup arrived with
     none set. */
  function primeGalleries() {
    var galleries = document.querySelectorAll('[data-lw-product]');
    for (var i = 0; i < galleries.length; i++) {
      var hasActive = galleries[i].querySelector('.lw-gallery__shot.is-active');
      if (!hasActive) selectShot(galleries[i], 0);
    }
  }

  /* --- variants --------------------------------------------------------- */

  function readVariants(root) {
    var node = root.querySelector('[data-lw-variants]');
    if (!node) return [];
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return [];
    }
  }

  function selectedOptions(root) {
    var values = [];
    var groups = root.querySelectorAll('[data-lw-option]');
    for (var i = 0; i < groups.length; i++) {
      var position = parseInt(groups[i].getAttribute('data-lw-option'), 10);
      if (
        groups[i].classList.contains('lw-swatch-btn--selected') ||
        groups[i].classList.contains('lw-color--selected')
      ) {
        values[position - 1] = groups[i].getAttribute('data-lw-value');
      }
    }
    return values;
  }

  function matchVariant(variants, chosen) {
    for (var i = 0; i < variants.length; i++) {
      var options = variants[i].options || [];
      var hit = true;
      for (var j = 0; j < chosen.length; j++) {
        if (chosen[j] != null && options[j] !== chosen[j]) {
          hit = false;
          break;
        }
      }
      if (hit) return variants[i];
    }
    return null;
  }

  function markUnavailable(root, variants, chosen) {
    /* Strike through a size that has no available variant given the other
       options already picked — the design shows sold-out sizes rather than
       hiding them, so the customer learns their size is gone up front. */
    var buttons = root.querySelectorAll('.lw-swatch-btn[data-lw-option]');
    for (var i = 0; i < buttons.length; i++) {
      var position = parseInt(buttons[i].getAttribute('data-lw-option'), 10);
      var probe = chosen.slice();
      probe[position - 1] = buttons[i].getAttribute('data-lw-value');

      var available = false;
      for (var v = 0; v < variants.length; v++) {
        var options = variants[v].options || [];
        var hit = true;
        for (var k = 0; k < probe.length; k++) {
          if (probe[k] != null && options[k] !== probe[k]) {
            hit = false;
            break;
          }
        }
        if (hit && variants[v].available) {
          available = true;
          break;
        }
      }
      buttons[i].classList.toggle('lw-swatch-btn--out', !available);
      buttons[i].disabled = !available;
    }
  }

  function updateProduct(root) {
    var variants = readVariants(root);
    if (!variants.length) return;

    var chosen = selectedOptions(root);
    var variant = matchVariant(variants, chosen);

    markUnavailable(root, variants, chosen);

    /* Swap the gallery to the chosen colour. Runs before the variant guard
       below so the photos still change even when a colour/size pair is
       unavailable — she should see the colour she clicked either way. */
    var cPos = colourPosition(root);
    if (cPos !== null && chosen[cPos - 1]) {
      applyColourFilter(root, chosen[cPos - 1]);
    }

    /* Reflect the picked value next to each option label */
    var labels = root.querySelectorAll('[data-lw-option-current]');
    for (var i = 0; i < labels.length; i++) {
      var pos = parseInt(labels[i].getAttribute('data-lw-option-current'), 10);
      if (chosen[pos - 1]) labels[i].textContent = ' · ' + chosen[pos - 1];
    }

    var idField = root.querySelector('[data-lw-variant-id]');
    var addButtons = root.querySelectorAll('[data-lw-add]');
    var priceSlot = root.querySelector('[data-lw-price]');
    var barPrice = root.querySelector('[data-lw-buybar-price]');
    var barSize = root.querySelector('[data-lw-buybar-size]');

    if (!variant) {
      for (var b = 0; b < addButtons.length; b++) addButtons[b].disabled = true;
      return;
    }

    if (idField) idField.value = variant.id;

    var qtyField = root.querySelector('[data-lw-qty]');
    var qty = qtyField ? parseInt(qtyField.value, 10) || 1 : 1;

    if (priceSlot) {
      priceSlot.innerHTML = priceMarkup(variant.price, variant.compare_at_price).replace(
        'lw-price--sm',
        ''
      );
    }
    if (barPrice) barPrice.innerHTML = priceMarkup(variant.price * qty);
    if (barSize && variant.title) barSize.textContent = variant.title.replace('Default Title', '');

    for (var c = 0; c < addButtons.length; c++) {
      addButtons[c].disabled = !variant.available;
      var label = addButtons[c].querySelector('[data-lw-add-label]');
      if (label) {
        label.innerHTML = variant.available
          ? 'Add to bag · ' + priceMarkup(variant.price * qty)
          : 'Sold out';
      }
    }

    /* Keep the address bar honest so a shared link opens on the same variant */
    if (window.history && window.history.replaceState) {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }
  }

  /* --- quantity --------------------------------------------------------- */

  function stepQuantity(button, delta) {
    var wrap = closest(button, '.lw-qty');
    if (!wrap) return;
    var input = wrap.querySelector('[data-lw-qty]');
    if (!input) return;

    var min = parseInt(input.getAttribute('min'), 10);
    var max = parseInt(input.getAttribute('max'), 10);
    if (isNaN(min)) min = 1;
    if (isNaN(max)) max = 99;

    var next = (parseInt(input.value, 10) || min) + delta;
    if (next < min) next = min;
    if (next > max) next = max;

    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* --- events ----------------------------------------------------------- */

  document.addEventListener('click', function (event) {
    var target = event.target;

    var thumb = closest(target, '[data-lw-thumb]');
    if (thumb) {
      var galleryRoot = closest(thumb, '[data-lw-product]');
      if (galleryRoot) selectShot(galleryRoot, thumb.getAttribute('data-lw-thumb'));
      return;
    }

    var option = closest(target, '[data-lw-option]');
    if (option) {
      var productRoot = closest(option, '[data-lw-product]');
      if (productRoot) {
        var position = option.getAttribute('data-lw-option');
        var siblings = productRoot.querySelectorAll('[data-lw-option="' + position + '"]');
        for (var i = 0; i < siblings.length; i++) {
          siblings[i].classList.remove('lw-swatch-btn--selected', 'lw-color--selected');
        }
        option.classList.add(
          option.classList.contains('lw-color') ? 'lw-color--selected' : 'lw-swatch-btn--selected'
        );
        updateProduct(productRoot);
      }
      return;
    }

    if (closest(target, '[data-lw-qty-up]')) {
      stepQuantity(closest(target, '[data-lw-qty-up]'), 1);
      return;
    }
    if (closest(target, '[data-lw-qty-down]')) {
      stepQuantity(closest(target, '[data-lw-qty-down]'), -1);
      return;
    }

    if (closest(target, '[data-lw-guide-open]')) {
      var guide = document.querySelector('[data-lw-guide]');
      if (guide && guide.showModal) guide.showModal();
      return;
    }
    if (closest(target, '[data-lw-guide-close]')) {
      var openGuide = document.querySelector('[data-lw-guide]');
      if (openGuide && openGuide.close) openGuide.close();
      return;
    }

    if (closest(target, '[data-lw-menu-open]')) {
      var menu = document.querySelector('[data-lw-menu]');
      if (menu && menu.showModal) menu.showModal();
      return;
    }
    if (closest(target, '[data-lw-menu-close]')) {
      var openMenu = document.querySelector('[data-lw-menu]');
      if (openMenu && openMenu.close) openMenu.close();
    }
  });

  /* Clicking the backdrop closes — <dialog> does not do this on its own. */
  document.addEventListener('click', function (event) {
    if (event.target && event.target.tagName === 'DIALOG') {
      var rect = event.target.getBoundingClientRect();
      var outside =
        event.clientY < rect.top ||
        event.clientY > rect.bottom ||
        event.clientX < rect.left ||
        event.clientX > rect.right;
      if (outside) event.target.close();
    }
  });

  document.addEventListener('change', function (event) {
    var target = event.target;

    if (target.matches && target.matches('[data-lw-sort]')) {
      var sortForm = closest(target, 'form');
      if (sortForm) sortForm.submit();
      return;
    }

    if (target.matches && target.matches('[data-lw-qty]')) {
      var productRoot = closest(target, '[data-lw-product]');
      if (productRoot) {
        updateProduct(productRoot);
        return;
      }
      /* On the cart, a quantity change has to reach the server. Submitting
         the whole form also persists the COD fields alongside it, which is
         exactly what we want — the landmark should survive a quantity tweak. */
      var cartForm = document.getElementById('lw-cart-form');
      if (cartForm && closest(target, '#lw-cart-form')) cartForm.submit();
    }
  });

  /* --- boot ------------------------------------------------------------- */

  function init() {
    primeGalleries();
    var roots = document.querySelectorAll('[data-lw-product]');
    for (var i = 0; i < roots.length; i++) updateProduct(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* The theme editor swaps section HTML in place. */
  document.addEventListener('shopify:section:load', init);
})();
